<template>
  <!-- 监护统计·历史查询：普通后台暖米白主题，禁止 el-container 包整页 -->
  <div class="ms-history">
    <!-- ===================== 筛选条 ===================== -->
    <div class="filter-bar">
      <el-date-picker
        v-model="range"
        type="daterange"
        value-format="yyyy-MM-dd"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        size="small"
        :unlink-panels="true"
        :picker-options="pickerOptions"
        class="date-range"
      />
      <DepartmentSelect
        v-model="dept"
        :option-list="deptOptions"
        placeholder="全部科室"
        class="dept-filter"
      />
      <el-radio-group v-model="groupBy" size="small" class="group-switch">
        <el-radio-button label="day">按天</el-radio-button>
        <el-radio-button label="month">按月</el-radio-button>
        <el-radio-button label="department">按科室</el-radio-button>
      </el-radio-group>
      <el-button size="small" type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">查询</el-button>
      <el-button size="small" icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
      <el-button size="small" type="success" icon="el-icon-download" :loading="exporting" @click="handleExport">导出 Excel</el-button>
    </div>

    <el-alert
      v-if="warning"
      type="warning"
      :closable="false"
      show-icon
      class="range-warning"
      :title="warning"
    />

    <el-tabs v-model="activeTab" class="ms-tabs" @tab-click="onTabClick">
      <!-- ===================== 汇总 ===================== -->
      <el-tab-pane label="汇总" name="summary">
        <el-table :data="summary.rows" border size="small" class="ms-table">
          <el-table-column type="index" label="序号" width="60" align="center" />
          <el-table-column prop="label" :label="groupLabel" min-width="160" />
          <el-table-column prop="monitor_person_times" label="监护人次" width="130" align="center" />
          <el-table-column label="监护仪台数" width="130" align="center">
            <template slot-scope="scope">
              {{ scope.row.monitor_devices == null ? '-' : scope.row.monitor_devices }}
            </template>
          </el-table-column>
          <el-table-column prop="dept_count" label="覆盖科室数" width="120" align="center" />
          <el-table-column prop="day_count" label="覆盖天数" width="110" align="center" />
        </el-table>

        <div v-if="summary.rows.length" class="sum-bar">
          <span>监护人次合计：<b>{{ summary.person_total }}</b> 人次</span>
          <span>监护仪台数合计：<b>{{ summary.device_total }}</b> 台</span>
          <span class="sum-note">（台数是存量快照，取区间内最新一天，不跨天相加）</span>
        </div>
        <div v-else class="mini-empty">该区间没有数据</div>
      </el-tab-pane>

      <!-- ===================== 明细 ===================== -->
      <el-tab-pane label="明细" name="detail">
        <el-table :data="histRows" border size="small" class="ms-table" v-loading="loading">
          <el-table-column type="index" label="序号" width="60" align="center" :index="rowIndex" />
          <el-table-column prop="stat_date" label="统计日期" width="130" align="center" />
          <el-table-column prop="department" label="科室" min-width="160" />
          <el-table-column prop="monitor_person_times" label="监护人次" width="120" align="center" />
          <el-table-column label="监护仪台数" width="120" align="center">
            <template slot-scope="scope">
              {{ scope.row.monitor_devices == null ? '-' : scope.row.monitor_devices }}
            </template>
          </el-table-column>
          <el-table-column label="人次入库时间" width="170" align="center">
            <template slot-scope="scope">{{ fmtTime(scope.row.person_updated_at) }}</template>
          </el-table-column>
          <el-table-column label="台数入库时间" width="170" align="center">
            <template slot-scope="scope">{{ fmtTime(scope.row.device_updated_at) }}</template>
          </el-table-column>
        </el-table>

        <div class="pager-bar">
          <span v-if="histTotal" class="pager-sum">
            共 <b>{{ histTotal }}</b> 条，区间人次合计 <b>{{ histPersonTotal }}</b>，
            台数合计 <b>{{ histDeviceTotal }}</b> 台
          </span>
          <el-pagination
            background
            layout="sizes, prev, pager, next, jumper"
            :current-page="page"
            :page-size="pageSize"
            :page-sizes="[20, 50, 100]"
            :total="histTotal"
            @current-change="onPageChange"
            @size-change="onSizeChange"
          />
        </div>
      </el-tab-pane>

      <!-- ===================== 日报原图 ===================== -->
      <el-tab-pane label="日报原图" name="files">
        <p class="files-tip">
          每次上传的《信息日报表》原图都会留档，即使预览后没有确认入库也能查到 —— OCR 漏格时用它核对。
        </p>
        <el-table :data="files" border size="small" class="ms-table" v-loading="filesLoading">
          <el-table-column type="index" label="序号" width="60" align="center" />
          <el-table-column prop="stat_date" label="统计日期" width="130" align="center" />
          <el-table-column prop="uploaded_by" label="上传人" width="120" align="center" />
          <el-table-column prop="row_count" label="入库科室数" width="120" align="center" />
          <el-table-column label="上传时间" width="170" align="center">
            <template slot-scope="scope">{{ fmtTime(scope.row.created_at) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="110" align="center">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="viewFile(scope.row)">查看原图</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pager-bar">
          <span v-if="filesTotal" class="pager-sum">共 <b>{{ filesTotal }}</b> 张</span>
          <el-pagination
            background
            layout="prev, pager, next"
            :current-page="filesPage"
            :page-size="20"
            :total="filesTotal"
            @current-change="onFilesPageChange"
          />
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import monitorStatApi from '../api/monitorStat'
import DepartmentSelect from './DepartmentSelect.vue'
import { Message } from 'element-ui'

export default {
  name: 'MonitorStatHistory',
  components: { DepartmentSelect },
  data() {
    return {
      // 筛选条件
      range: [], // [start, end]，空数组=后端默认「本月1号~今天」
      dept: '',
      deptOptions: [], // 合并显示名，来自 /monitor-stat/departments/
      groupBy: 'day',

      loading: false,
      exporting: false,
      warning: '',
      activeTab: 'summary',

      // 汇总
      summary: {
        rows: [],
        person_total: 0,
        device_total: 0,
        day_count: 0,
        dept_count: 0,
      },

      // 明细
      histRows: [],
      histTotal: 0,
      histPersonTotal: 0,
      histDeviceTotal: 0,
      page: 1,
      pageSize: 20,

      // 原图留档
      files: [],
      filesTotal: 0,
      filesPage: 1,
      filesLoading: false,

      pickerOptions: {
        shortcuts: [
          {
            text: '本月',
            onClick(picker) {
              const now = new Date()
              picker.$emit('pick', [new Date(now.getFullYear(), now.getMonth(), 1), now])
            },
          },
          {
            text: '上月',
            onClick(picker) {
              const now = new Date()
              picker.$emit('pick', [
                new Date(now.getFullYear(), now.getMonth() - 1, 1),
                new Date(now.getFullYear(), now.getMonth(), 0),
              ])
            },
          },
          {
            text: '今年',
            onClick(picker) {
              const now = new Date()
              picker.$emit('pick', [new Date(now.getFullYear(), 0, 1), now])
            },
          },
        ],
      },
    }
  },
  computed: {
    groupLabel() {
      return { day: '统计日期', month: '统计月份', department: '科室' }[this.groupBy] || '统计维度'
    },
  },
  mounted() {
    this.initDefaultRange()
    this.fetchDeptOptions()
    this.fetchAll()
  },
  methods: {
    // ---------- 初始化 ----------
    initDefaultRange() {
      const d = new Date()
      const p = n => (n < 10 ? '0' + n : '' + n)
      const start = `${d.getFullYear()}-${p(d.getMonth() + 1)}-01`
      this.range = [start, `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`]
    },
    async fetchDeptOptions() {
      try {
        const res = await monitorStatApi.getDeptOptions()
        this.deptOptions = Array.isArray(res.data) ? res.data : []
      } catch (e) {
        // 无权限/后端未起时静默：下拉为空不影响日期查询
        this.deptOptions = []
      }
    },
    // ---------- 参数拼装：空值一律不下发 ----------
    buildParams(extra) {
      const p = { group_by: this.groupBy }
      if (Array.isArray(this.range) && this.range.length === 2 && this.range[0] && this.range[1]) {
        p.start_date = this.range[0]
        p.end_date = this.range[1]
      }
      if (this.dept) p.department = this.dept
      return Object.assign(p, extra || {})
    },
    // ---------- 查询 ----------
    handleSearch() {
      this.page = 1
      this.fetchAll()
    },
    handleReset() {
      this.dept = ''
      this.groupBy = 'day'
      this.page = 1
      this.initDefaultRange()
      this.fetchAll()
    },
    fetchAll() {
      this.fetchSummary()
      this.fetchHistory()
      if (this.activeTab === 'files') this.fetchFiles()
    },
    async fetchSummary() {
      this.loading = true
      try {
        const res = await monitorStatApi.getSummary(this.buildParams())
        const d = res.data || {}
        this.summary = {
          rows: Array.isArray(d.rows) ? d.rows : [],
          person_total: d.person_total || 0,
          device_total: d.device_total || 0,
          day_count: d.day_count || 0,
          dept_count: d.dept_count || 0,
        }
        this.warning = d.warning || ''
      } catch (e) {
        this.summary = { rows: [], person_total: 0, device_total: 0, day_count: 0, dept_count: 0 }
        this.warning = ''
        Message.error(this.errMsg(e, '汇总查询失败'))
      } finally {
        this.loading = false
      }
    },
    async fetchHistory() {
      try {
        const res = await monitorStatApi.getHistory(this.buildParams({
          page: this.page,
          page_size: this.pageSize,
        }))
        const d = res.data || {}
        this.histRows = Array.isArray(d.results) ? d.results : []
        this.histTotal = d.count || 0
        this.histPersonTotal = d.person_total || 0
        this.histDeviceTotal = d.device_total || 0
      } catch (e) {
        this.histRows = []
        this.histTotal = 0
        Message.error(this.errMsg(e, '明细查询失败'))
      }
    },
    async fetchFiles() {
      this.filesLoading = true
      try {
        const res = await monitorStatApi.listFiles({
          start_date: this.range && this.range[0],
          end_date: this.range && this.range[1],
          page: this.filesPage,
          page_size: 20,
        })
        const d = res.data || {}
        this.files = Array.isArray(d.results) ? d.results : []
        this.filesTotal = d.count || 0
      } catch (e) {
        this.files = []
        this.filesTotal = 0
        Message.error(this.errMsg(e, '原图列表查询失败'))
      } finally {
        this.filesLoading = false
      }
    },
    onTabClick() {
      if (this.activeTab === 'files') this.fetchFiles()
    },
    // ---------- 分页 ----------
    onPageChange(p) {
      this.page = p
      this.fetchHistory()
    },
    onSizeChange(s) {
      this.pageSize = s
      this.page = 1
      this.fetchHistory()
    },
    onFilesPageChange(p) {
      this.filesPage = p
      this.fetchFiles()
    },
    rowIndex(i) {
      return (this.page - 1) * this.pageSize + i + 1
    },
    // ---------- 导出 ----------
    async handleExport() {
      this.exporting = true
      try {
        const res = await monitorStatApi.exportHistory(this.buildParams())
        // 后端返回 octet-stream；前端不要改 MIME，否则部分浏览器会拦下载
        const blob = new Blob([res.data], { type: 'application/octet-stream' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `监护统计_${(this.range || []).join('_') || '全部'}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        Message.error(this.errMsg(e, '导出失败，请稍后重试'))
      } finally {
        this.exporting = false
      }
    },
    // ---------- 原图 ----------
    viewFile(row) {
      if (!row.file_url) {
        Message.warning('原图路径缺失')
        return
      }
      window.open(row.file_url, '_blank')
    },
    // ---------- 工具 ----------
    fmtTime(v) {
      if (!v) return '-'
      return String(v).replace('T', ' ').slice(0, 16)
    },
    errMsg(e, fallback) {
      const d = e && e.response && e.response.data
      const msg = d && (d.detail || d.non_field_errors)
      if (!msg) return fallback
      return Array.isArray(msg) ? msg.join('；') : msg
    },
    // 供父组件在入库/上传成功后刷新
    reload() {
      this.fetchDeptOptions()
      this.page = 1
      this.fetchAll()
    },
  },
}
</script>

<style scoped>
.ms-history {
  padding: 0;
}
/* 筛选条：与既有列表页风格一致，全部小尺寸控件 */
.filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.date-range {
  width: 250px;
}
.dept-filter {
  width: 180px;
}
.group-switch {
  margin-left: 4px;
}
.range-warning {
  margin-bottom: 12px;
}
.ms-tabs /deep/ .el-tabs__header {
  margin-bottom: 12px;
}
.ms-table {
  width: 100%;
}
/* 汇总条 */
.sum-bar {
  display: flex;
  align-items: baseline;
  gap: 24px;
  margin-top: 12px;
  font-size: 13px;
  color: #8fd6e8;
  flex-wrap: wrap;
}
.sum-bar b {
  color: #22d3ee;
  font-size: 16px;
  text-shadow: 0 0 10px rgba(34, 211, 238, 0.4);
}
.sum-note {
  font-size: 12px;
  color: #5b7a95;
}
/* 分页条 */
.pager-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  gap: 12px;
  flex-wrap: wrap;
}
.pager-sum {
  font-size: 13px;
  color: #8fd6e8;
}
.pager-sum b {
  color: #22d3ee;
}
.files-tip {
  margin: 0 0 10px;
  font-size: 12px;
  color: #5b7a95;
  line-height: 1.6;
}
.mini-empty {
  color: #5b7a95;
  font-size: 13px;
  padding: 24px 0;
  text-align: center;
}
</style>
